import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AUDIENCES, GUIDES, guidesForPath } from '../help/guides.js';

// Help (Joe, 4 Nov 2026): short guides to doing things in Blake, grouped by
// who does the job. /help?page=/orders shows the guides for a page (the "?" in
// the header); /help?topic=<id> opens one guide.

const GREEN = 'var(--pld-green)';

// "Press [[Receive]]" → text with the button names drawn as buttons.
function Step({ text }) {
  const parts = String(text).split(/\[\[(.+?)\]\]/g);
  return <>{parts.map((p, i) => (i % 2
    ? <span key={i} style={{ display: 'inline-block', padding: '0 7px', margin: '1px 1px', border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface-2)', fontSize: '0.92em', fontWeight: 600, whiteSpace: 'nowrap' }}>{p}</span>
    : <span key={i}>{p}</span>))}</>;
}

const plain = (g) => [g.title, g.summary, ...g.steps, ...(g.tips || [])].join(' ').replace(/\[\[|\]\]/g, '').toLowerCase();

function GuideCard({ g, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(g.id)} className="help-card">
      <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>{g.title}</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.45 }}>{g.summary}</div>
    </button>
  );
}

function Guide({ g, onBack, backLabel }) {
  const related = GUIDES.filter((o) => o.id !== g.id && o.pages.some((p) => g.pages.includes(p))).slice(0, 4);
  return (
    <div style={{ maxWidth: 760 }}>
      <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: GREEN, cursor: 'pointer', padding: 0, fontSize: 14, marginBottom: 14 }}>← {backLabel}</button>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', fontWeight: 600 }}>
        {AUDIENCES.find((a) => a.key === g.audience)?.label}
      </div>
      <h2 style={{ margin: '4px 0 6px', fontSize: 22 }}>{g.title}</h2>
      <p style={{ margin: '0 0 18px', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.5 }}>{g.summary}</p>

      <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {g.steps.map((s, i) => (
          <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
            <span style={{ flex: '0 0 26px', height: 26, borderRadius: 99, background: GREEN, color: '#fff', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
            <span style={{ fontSize: 14, lineHeight: 1.6, paddingTop: 2 }}><Step text={s} /></span>
          </li>
        ))}
      </ol>

      {g.tips?.length > 0 && (
        <div style={{ marginTop: 18, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#166534', marginBottom: 6 }}>Good to know</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
            {g.tips.map((t, i) => <li key={i}><Step text={t} /></li>)}
          </ul>
        </div>
      )}

      <div style={{ marginTop: 18, fontSize: 13, color: 'var(--text-muted)' }}>
        Go to: {g.pages.map((p, i) => <span key={p}>{i > 0 && ' · '}<Link to={p} style={{ color: GREEN }}>{p.slice(1).replace(/-/g, ' ') || 'dashboard'}</Link></span>)}
      </div>

      {related.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Related</div>
          <div className="help-grid">{related.map((o) => <GuideCard key={o.id} g={o} onOpen={(id) => onBack(id)} />)}</div>
        </div>
      )}
    </div>
  );
}

export default function Help() {
  const [params, setParams] = useSearchParams();
  const topic = params.get('topic');
  const page = params.get('page');
  const q = params.get('q') || '';
  const who = params.get('who') || '';

  const set = (next) => {
    const p = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => { if (v) p.set(k, v); });
    setParams(p, { replace: !('topic' in next) });
  };

  const forPage = page ? guidesForPath(page) : null;
  const shown = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    return (forPage || GUIDES).filter((g) => (!who || g.audience === who) && words.every((w) => plain(g).includes(w)));
  }, [q, who, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = GUIDES.find((g) => g.id === topic);
  if (open) {
    return (
      <div style={{ padding: 4 }}>
        <style>{css}</style>
        <Guide g={open} backLabel={page ? 'Guides for this page' : 'All guides'}
          onBack={(id) => set(typeof id === 'string' ? { topic: id, page } : { page, q, who })} />
      </div>
    );
  }

  return (
    <div style={{ padding: 4, maxWidth: 1000 }}>
      <style>{css}</style>
      <h2 style={{ margin: '0 0 4px', fontSize: 22 }}>How to…</h2>
      <p style={{ margin: '0 0 16px', color: 'var(--text-muted)', fontSize: 14 }}>
        Step-by-step guides to everyday jobs in Blake. Button names are shown <Step text="[[like this]]" />, as they appear on screen.
      </p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
        <input type="search" value={q} onChange={(e) => set({ q: e.target.value, who, page })} placeholder="Search — e.g. receive, despatch, Xero, W1"
          autoFocus style={{ flex: '1 1 280px', height: 40, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface)' }} />
        {[{ key: '', label: 'Everyone' }, ...AUDIENCES].map((a) => (
          <button key={a.key} type="button" onClick={() => set({ q, who: a.key, page })}
            style={{ height: 40, padding: '0 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, border: `1px solid ${who === a.key ? GREEN : 'var(--border)'}`, background: who === a.key ? GREEN : 'var(--surface)', color: who === a.key ? '#fff' : 'var(--text)' }}>
            {a.label}
          </button>
        ))}
      </div>

      {page && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 14 }}>
          <span>Guides for <strong>{page.slice(1).replace(/-/g, ' ')}</strong></span>
          <button type="button" onClick={() => set({ q, who })} style={{ background: 'none', border: 'none', color: GREEN, cursor: 'pointer', padding: 0, fontSize: 14 }}>Show all guides</button>
        </div>
      )}

      {shown.length === 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
          {page && !q ? 'No guides for this page yet.' : 'Nothing matches that.'}{' '}
          {page && <button type="button" onClick={() => set({ q, who })} style={{ background: 'none', border: 'none', color: GREEN, cursor: 'pointer', padding: 0, fontSize: 14 }}>Look through all guides</button>}
        </p>
      )}

      {AUDIENCES.filter((a) => !who || a.key === who).map((a) => {
        const list = shown.filter((g) => g.audience === a.key);
        if (!list.length) return null;
        return (
          <section key={a.key} style={{ marginBottom: 26 }}>
            <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', margin: '0 0 10px' }}>{a.label}</h3>
            <div className="help-grid">{list.map((g) => <GuideCard key={g.id} g={g} onOpen={(id) => set({ topic: id, page, q, who })} />)}</div>
          </section>
        );
      })}

      <p style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)' }}>
        Can't find it? Press <Step text="[[✦ Ask AI]]" /> at the top and ask in your own words.
      </p>
    </div>
  );
}

const css = `
  .help-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 10px; }
  .help-card { text-align: left; background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 14px 16px; cursor: pointer; transition: border-color .15s, box-shadow .15s; }
  .help-card:hover { border-color: var(--pld-green); box-shadow: 0 1px 4px rgba(0,0,0,.06); }
`;
